/**
 * EMP-MASTER-001 — Employee Import modal: upload → preview (Created /
 * Updated / Rejected per row) → confirm → summary. Template download included.
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Upload, FileDown, Loader2, CheckCircle2, RefreshCw, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const ACTION_BADGE = {
  create: { label: 'Created', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  update: { label: 'Updated', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700 border-red-200' },
};

const downloadBlob = (data, filename) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadImportTemplate = async () => {
  try {
    const res = await axios.get(`${API}/api/hr/employees/import-template`, {
      headers: getAuthHeaders(), responseType: 'blob',
    });
    downloadBlob(res.data, 'employee_import_template.xlsx');
  } catch {
    toast.error('Failed to download template');
  }
};

const EmployeeImportModal = ({ open, onOpenChange, onImported }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [summary, setSummary] = useState(null);

  const reset = () => { setFile(null); setPreview(null); setSummary(null); };

  const runImport = async (mode) => {
    if (!file) { toast.error('Choose a .xlsx or .csv file first'); return; }
    try {
      setBusy(true);
      const fd = new FormData();
      fd.append('file', file);
      fd.append('mode', mode);
      const res = await axios.post(`${API}/api/hr/employees/import`, fd, { headers: getAuthHeaders() });
      if (mode === 'preview') {
        setPreview(res.data);
        setSummary(null);
      } else {
        setSummary(res.data);
        setPreview(null);
        toast.success(`Import complete — ${res.data.created} created, ${res.data.updated} updated, ${res.data.failed} failed`);
        onImported?.();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const data = summary || preview;
  const failedRows = (data?.rows || []).filter(r => r.action === 'rejected');

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Import Employees</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.csv"
              className="hidden"
              data-testid="employee-import-file-input"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setSummary(null); }}
            />
            <Button variant="outline" onClick={() => fileRef.current?.click()} className="gap-2" data-testid="employee-import-choose-file">
              <Upload className="w-4 h-4" />
              {file ? file.name : 'Choose file (.xlsx / .csv)'}
            </Button>
            <Button
              variant="ghost"
              onClick={downloadImportTemplate}
              className="gap-2 text-slate-500"
              data-testid="employee-import-template-btn"
            >
              <FileDown className="w-4 h-4" />
              Download template
            </Button>
          </div>

          {data && (
            <>
              <div className="grid grid-cols-4 gap-3 text-center">
                {[
                  ['Processed', data.total, 'text-slate-700'],
                  [summary ? 'Created' : 'Will create', data.created, 'text-emerald-600'],
                  [summary ? 'Updated' : 'Will update', data.updated, 'text-blue-600'],
                  ['Failed', data.failed, 'text-red-600'],
                ].map(([label, value, cls]) => (
                  <div key={label} className="bg-slate-50 border border-slate-100 rounded-lg p-3">
                    <p className={`text-2xl font-bold ${cls}`} data-testid={`import-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>{value}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{label}</p>
                  </div>
                ))}
              </div>

              <div className="border border-slate-200 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="tf-thead sticky top-0">
                    <tr className="text-left text-xs text-slate-500 uppercase">
                      <th className="px-3 py-2">Row</th>
                      <th className="px-3 py-2">Employee</th>
                      <th className="px-3 py-2">Action</th>
                      <th className="px-3 py-2">Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.rows || []).map(r => {
                      const badge = ACTION_BADGE[r.action] || ACTION_BADGE.rejected;
                      return (
                        <tr key={r.row_number} className="border-t border-slate-100">
                          <td className="px-3 py-2 text-slate-400">{r.row_number}</td>
                          <td className="px-3 py-2">
                            <span className="font-medium text-slate-700">{r.full_name || '—'}</span>
                            <span className="text-slate-400 ml-2 text-xs">{r.employee_id || r.work_email}</span>
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className={badge.cls}>{badge.label}</Badge>
                          </td>
                          <td className="px-3 py-2 text-xs text-red-600">
                            {(r.errors || []).join('; ')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {summary && (
                <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg p-3" data-testid="import-success-banner">
                  <CheckCircle2 className="w-4 h-4" />
                  Import committed. {failedRows.length > 0 && `${failedRows.length} row(s) failed — see error reasons above.`}
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {!summary && (
            <>
              <Button variant="secondary" onClick={() => runImport('preview')} disabled={busy || !file} className="gap-2" data-testid="employee-import-preview-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Preview
              </Button>
              <Button
                onClick={() => runImport('commit')}
                disabled={busy || !preview || (preview.created + preview.updated) === 0}
                className="gap-2"
                data-testid="employee-import-commit-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Confirm Import
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeImportModal;
